import { getRouteApi } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    reclassifyAgentNotes,
    retryAgentNoteClassification,
    useAdminAgentNote,
    useAdminAgentNotes,
} from '@apps/web/features/admin_agent_notes/api/agent_notes_queries';
import { isInlinePanelVisible } from '@apps/web/features/admin_agent_notes/model/constants';
import { noteHasReclassifiableWork } from '@apps/web/features/admin_agent_notes/model/note_row_summary';
import {
    type AgentNotesSearch,
    PER_PAGE,
} from '@apps/web/features/admin_agent_notes/model/search_params';
import { useAgentNotesQueryParams } from '@apps/web/features/admin_agent_notes/model/use_agent_notes_query_params';
import { toast } from '@apps/web/lib/app_notifications';

const route = getRouteApi('/_app/admin/agent-notes/');

// Keep the list live so the feed reads like a monitor.
const LIVE_REFRESH_INTERVAL_MS = 30_000;

/**
 * Controller for the Notes tab: URL-owned filters, the live-refreshing
 * notes list query, feed selection with prev/next navigation, page
 * clamping, the retry-classification command, and multi-select bulk
 * reclassify over notes with failed or held items.
 */
export function useAgentNotesPage() {
    const search = route.useSearch();
    const navigate = route.useNavigate();
    const [isRetrying, setIsRetrying] = useState(false);
    const [isReclassifying, setIsReclassifying] = useState(false);
    const [selectedNoteIds, setSelectedNoteIds] = useState<ReadonlySet<string>>(
        () => new Set(),
    );
    const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);

    const queryParams = useAgentNotesQueryParams(search);
    const notesQuery = useAdminAgentNotes(queryParams, {
        keepPreviousData: true,
        refreshIntervalMs: LIVE_REFRESH_INTERVAL_MS,
    });
    const { mutate: refreshNotes } = notesQuery;

    useEffect(() => {
        if (notesQuery.data !== undefined) {
            setLastRefreshedAt(Date.now());
        }
    }, [notesQuery.data]);

    const patchSearch = useCallback(
        (patch: Partial<AgentNotesSearch>) => {
            void navigate({
                search: (prev) => ({ ...prev, ...patch }),
                replace: true,
                resetScroll: false,
            });
        },
        [navigate],
    );

    const noteData = notesQuery.data;
    const notes = useMemo(() => noteData?.agent_notes ?? [], [noteData]);
    // Selection is scoped to the notes currently in view: a stale id left
    // over from a prior page or filter never gets reclassified silently.
    const selectedVisibleIds = useMemo(
        () =>
            notes
                .filter((note) => selectedNoteIds.has(note.id))
                .map((note) => note.id),
        [notes, selectedNoteIds],
    );
    const total = notesQuery.data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
    const statusCounts = notesQuery.data?.status_counts ?? [];
    const selectedIndex = notes.findIndex((note) => note.id === search.note);
    const selectedListNote =
        selectedIndex >= 0 ? (notes[selectedIndex] ?? null) : null;
    // With no explicit selection the feed shows the first note, so prev/next
    // navigate relative to it.
    const effectiveIndex =
        selectedIndex >= 0 ? selectedIndex : notes.length > 0 ? 0 : -1;
    const detailNoteId =
        selectedListNote?.id ?? (notes.length > 0 ? notes[0]!.id : null);
    const noteDetailQuery = useAdminAgentNote(detailNoteId, {
        refreshIntervalMs: LIVE_REFRESH_INTERVAL_MS,
    });
    const selectedNote =
        noteDetailQuery.data?.id === detailNoteId
            ? noteDetailQuery.data
            : (selectedListNote ?? notes[0] ?? null);
    const { mutate: refreshSelectedNote } = noteDetailQuery;
    const selectedNoteDetailError =
        noteDetailQuery.hasRequestFailed &&
        noteDetailQuery.data?.id !== detailNoteId;
    const refresh = useCallback(async () => {
        try {
            await Promise.all([refreshNotes(), refreshSelectedNote()]);
        } catch {
            toast.error('We couldn\u2019t refresh notes. Please try again.', {
                persistNotification: false,
            });
        }
    }, [refreshNotes, refreshSelectedNote]);

    // Resolve an initial off-page deep link once; later stale selections clear.
    const deepLinkResolvedRef = useRef(false);
    useEffect(() => {
        // With keepPreviousData, data can belong to a previous search key
        // (isLoading is true while the current key fetches) — deciding
        // clamping or deep-link resolution against those rows would be
        // wrong, so wait for data that matches the current params.
        if (notesQuery.data === undefined || notesQuery.isLoading) {
            return;
        }
        if (search.page > totalPages) {
            patchSearch({ page: totalPages });
            return;
        }
        if (search.note !== undefined) {
            const isOnPage = notesQuery.data.agent_notes.some(
                (note) => note.id === search.note,
            );
            if (
                !deepLinkResolvedRef.current &&
                (!isOnPage || !isInlinePanelVisible())
            ) {
                deepLinkResolvedRef.current = true;
                void navigate({
                    to: '/admin/agent-notes/$note-id',
                    params: { 'note-id': search.note },
                    search: { ...search, note: undefined },
                });
                return;
            }
            if (!isOnPage) {
                patchSearch({ note: undefined });
            }
        }
        deepLinkResolvedRef.current = true;
    }, [
        navigate,
        notesQuery.data,
        notesQuery.isLoading,
        patchSearch,
        search,
        totalPages,
    ]);

    const navigateNote = useCallback(
        (direction: 'previous' | 'next') => {
            if (effectiveIndex < 0) {
                return;
            }
            const nextIndex =
                direction === 'previous'
                    ? effectiveIndex - 1
                    : effectiveIndex + 1;
            const nextNote = notes[nextIndex];
            if (nextNote) {
                patchSearch({ note: nextNote.id });
            }
        },
        [notes, patchSearch, effectiveIndex],
    );

    // Returns whether the retry was accepted, so an editor can keep the
    // admin's text on failure instead of discarding it.
    const retryClassification = useCallback(
        async (noteId: string, content?: string): Promise<boolean> => {
            setIsRetrying(true);
            try {
                const result = await retryAgentNoteClassification(
                    noteId,
                    content,
                );
                if (result.published) {
                    toast.success(
                        'Note reset to pending and queued for classification',
                        { persistNotification: false },
                    );
                } else {
                    toast.warning(
                        'Note reset to pending, but the queue publish failed. The pending sweep will pick it up.',
                        { persistNotification: false },
                    );
                }
                await Promise.all([refreshNotes(), refreshSelectedNote()]);
                return true;
            } catch {
                toast.error('Retry failed. The note was not changed.', {
                    persistNotification: false,
                });
                return false;
            } finally {
                setIsRetrying(false);
            }
        },
        [refreshNotes, refreshSelectedNote],
    );

    const toggleNoteSelected = useCallback((noteId: string) => {
        setSelectedNoteIds((previous) => {
            const next = new Set(previous);
            if (next.has(noteId)) {
                next.delete(noteId);
            } else {
                next.add(noteId);
            }
            return next;
        });
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedNoteIds(new Set());
    }, []);

    // Select-all covers the page's selectable notes; ids off this page are
    // dropped so a filter change can't queue an unseen note.
    const selectableIds = useMemo(
        () =>
            notes
                .filter((note) => noteHasReclassifiableWork(note))
                .map((note) => note.id),
        [notes],
    );
    const allVisibleSelected =
        selectableIds.length > 0 &&
        selectableIds.every((id) => selectedNoteIds.has(id));
    const toggleSelectAllVisible = useCallback(() => {
        setSelectedNoteIds((previous) => {
            const wasAllSelected =
                selectableIds.length > 0 &&
                selectableIds.every((id) => previous.has(id));
            return wasAllSelected ? new Set() : new Set(selectableIds);
        });
    }, [selectableIds]);

    const reclassifySelected = useCallback(async () => {
        if (selectedVisibleIds.length === 0) {
            return;
        }
        setIsReclassifying(true);
        try {
            const response = await reclassifyAgentNotes(selectedVisibleIds);
            const itemsRequeued = response.results.reduce(
                (sum, result) => sum + result.items_requeued,
                0,
            );
            const notesReclassified = response.results.filter(
                (result) => result.reclassified,
            ).length;
            const unpublished = response.results.filter(
                (result) => !result.published,
            ).length;
            const parts = [
                notesReclassified > 0
                    ? `Reclassifying ${notesReclassified} ${
                          notesReclassified === 1 ? 'note' : 'notes'
                      }`
                    : null,
                itemsRequeued > 0
                    ? `requeued ${itemsRequeued} held ${
                          itemsRequeued === 1 ? 'item' : 'items'
                      }`
                    : null,
            ].filter((part) => part !== null);
            if (parts.length === 0) {
                toast.info('Nothing to reclassify in the selected notes.', {
                    persistNotification: false,
                });
            } else if (unpublished > 0) {
                toast.warning(
                    `${parts.join(' and ')}. ${unpublished} ${
                        unpublished === 1 ? 'note' : 'notes'
                    } could not be queued yet. The pending sweep will pick ${
                        unpublished === 1 ? 'it' : 'them'
                    } up.`,
                    { persistNotification: false },
                );
            } else {
                toast.success(parts.join(' and '), {
                    persistNotification: false,
                });
            }
            setSelectedNoteIds(new Set());
            await Promise.all([refreshNotes(), refreshSelectedNote()]);
        } catch {
            toast.error('Reclassify failed. No notes were changed.', {
                persistNotification: false,
            });
        } finally {
            setIsReclassifying(false);
        }
    }, [selectedVisibleIds, refreshNotes, refreshSelectedNote]);

    return {
        search,
        lastRefreshedAt,
        notes,
        total,
        totalPages,
        statusCounts,
        selectedNote,
        hasPreviousNote: effectiveIndex > 0,
        hasNextNote: effectiveIndex >= 0 && effectiveIndex < notes.length - 1,
        isLoading: notesQuery.isLoading,
        isRefreshing: notesQuery.isValidating,
        hasError:
            notesQuery.error !== undefined ||
            (notesQuery.hasRequestFailed && notesQuery.data === undefined),
        isRetrying,
        selectedNoteIds,
        selectedCount: selectedVisibleIds.length,
        selectableCount: selectableIds.length,
        allVisibleSelected,
        toggleSelectAllVisible,
        isReclassifying,
        patchSearch,
        navigateNote,
        retryClassification,
        toggleNoteSelected,
        clearSelection,
        reclassifySelected,
        refresh,
        selectedNoteDetailError,
    };
}
